import { useEffect, useState } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { ApiTypes } from '@/types/server/api.types';
import { toast } from 'sonner';
import { logError } from '@/utils/logger.utils';
import { handleLogout } from '@/utils/auth.utils';
import { isBrowserAndSystemValid } from '@/utils/browser.utils';
import { useNavigate } from 'react-router';
import posthog from 'posthog-js';

enum InterviewStatusResponseCode {
	notFound = 'getInterviewStatus_notFound',
	invited = 'getInterviewStatus_invited',
	scheduled = 'getInterviewStatus_scheduled',
	started = 'getInterviewStatus_started',
	ended = 'getInterviewStatus_ended',
}

interface InterviewDetailsSectionProps {
	setIsUserLoggedIn: (isUserLoggedIn: boolean) => void;
	setUserDetails: (
		userDetails: ApiTypes.TGetCandidateDetailsResponse['data'] | null
	) => void;
}

export const InterviewDetailsSection = ({
	setIsUserLoggedIn,
	setUserDetails,
}: InterviewDetailsSectionProps) => {
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState(true);
	const [interviewStatus, setInterviewStatus] = useState<
		ApiTypes.TGetInterviewStatusResponse['data'] | null
	>(null);
	const [responseCode, setResponseCode] =
		useState<InterviewStatusResponseCode | null>(null);

	// Scheduling state
	const [selectedDate, setSelectedDate] = useState<string>('');
	const [selectedTime, setSelectedTime] = useState<string>('');
	const [isScheduling, setIsScheduling] = useState(false);
	const [showRescheduleForm, setShowRescheduleForm] = useState(false);

	// Time tracking state
	const [currentTime, setCurrentTime] = useState<Date>(new Date());

	// Helper function to group slots by date
	const groupSlotsByDate = (slots: number[]) => {
		const groupedSlots: { [date: string]: number[] } = {};

		slots.forEach((slot) => {
			const date = new Date(slot);
			const dateKey = date.toDateString();

			if (!groupedSlots[dateKey]) {
				groupedSlots[dateKey] = [];
			}
			groupedSlots[dateKey].push(slot);
		});

		return groupedSlots;
	};

	// Helper function to format time from epoch timestamp
	const formatTime = (timestamp: number) => {
		const date = new Date(timestamp);
		return date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		});
	};

	// Helper function to format date for display
	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	};

	// Schedule interview function
	const scheduleInterview = async () => {
		if (
			!selectedDate ||
			!selectedTime ||
			!interviewStatus?.interviewId ||
			isScheduling
		) {
			return;
		}

		try {
			setIsScheduling(true);
			const token = window.localStorage.getItem('token');

			const slotTimestamp = parseInt(selectedTime);

			const response = await fetch(
				`${import.meta.env.VITE_SERVER_API_PREFIX}/interviews/schedule/${
					interviewStatus.interviewId
				}`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						slot: slotTimestamp,
					}),
				}
			);

			const data =
				(await response.json()) as ApiTypes.TScheduleInterviewResponse;

			if (data.httpResponseCode !== 200) {
				toast('Scheduling Error', {
					description: data.message,
					id: 'schedule-interview-error',
				});

				if (data.httpResponseCode === 401) {
					handleLogout();
					setIsUserLoggedIn(false);
					setUserDetails(null);
				}

				if (data.identificationCode === 'scheduleInterview_slotNotAvailable') {
					// refresh the interview status to show the updated slots
					await getInterviewStatus();
				}

				return;
			}

			// Determine if this was scheduling or rescheduling based on the current state
			const isRescheduling =
				responseCode === InterviewStatusResponseCode.scheduled;

			if (isRescheduling) {
				toast('Interview Rescheduled', {
					description: 'Your interview has been rescheduled successfully!',
					id: 'reschedule-interview-success',
				});
				// Hide the reschedule form
				setShowRescheduleForm(false);
				setSelectedDate('');
				setSelectedTime('');
			} else {
				toast('Interview Scheduled', {
					description: 'Your interview has been scheduled successfully!',
					id: 'schedule-interview-success',
				});
			}

			// Refresh the interview status to show updated state
			await getInterviewStatus();
		} catch (error) {
			logError(error);

			toast('Scheduling Error', {
				description: 'Failed to schedule interview. Please try again.',
				id: 'schedule-interview-error',
			});
		} finally {
			setIsScheduling(false);
		}
	};

	const getInterviewStatus = async () => {
		try {
			const token = window.localStorage.getItem('token');

			const response = await fetch(
				`${import.meta.env.VITE_SERVER_API_PREFIX}/interviews/status`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
				}
			);

			const data =
				(await response.json()) as ApiTypes.TGetInterviewStatusResponse;

			if (data.httpResponseCode !== 200) {
				if (data.httpResponseCode === 404 || data.httpResponseCode === 500) {
					setInterviewStatus(null);
					return;
				}

				toast('Error getting interview status', {
					description: data.message,
					id: 'interview-status-error',
				});

				if (data.httpResponseCode === 401) {
					handleLogout();
					setIsUserLoggedIn(false);
					setUserDetails(null);
				}

				return;
			}

			// Store the data and response code separately for state-specific rendering
			setInterviewStatus(data.data);
			setResponseCode(data.identificationCode as InterviewStatusResponseCode);
		} catch (error) {
			logError(error);

			toast('Interview Status Error', {
				description:
					'Failed to get interview status. Please refresh the page and try again.',
				id: 'interview-status-error',
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getInterviewStatus();
	}, []);

	// Reset selection when interview status changes
	useEffect(() => {
		setSelectedDate('');
		setSelectedTime('');
		setShowRescheduleForm(false);
	}, [responseCode]);

	// Time tracking for scheduled interviews
	useEffect(() => {
		let intervalId: NodeJS.Timeout | null = null;

		if (responseCode === InterviewStatusResponseCode.scheduled) {
			// Update time every second
			intervalId = setInterval(() => {
				setCurrentTime(new Date());
			}, 1000);
		}

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, [responseCode]);

	// Helper function to get time difference in minutes
	const getTimeDifferenceInMinutes = (
		scheduledTime: Date,
		currentTime: Date
	): number => {
		return Math.floor(
			(scheduledTime.getTime() - currentTime.getTime()) / (1000 * 60)
		);
	};

	// Helper function to determine interview join state
	const getInterviewJoinState = (scheduledAt: number | null) => {
		if (!scheduledAt) return null;

		if (
			interviewStatus?.minutesUntilQueueClears &&
			interviewStatus.minutesUntilQueueClears > 0
		) {
			return 'interviewQueued';
		}

		const scheduledTime = new Date(scheduledAt);
		const timeDiffMinutes = getTimeDifferenceInMinutes(
			scheduledTime,
			currentTime
		);

		if (timeDiffMinutes < 15 && timeDiffMinutes > 0) {
			return 'canJoinEarly'; // Less than 15 minutes before scheduled time
		} else if (timeDiffMinutes <= 0 && timeDiffMinutes > -15) {
			return 'interviewStarted'; // 0 to 15 minutes after scheduled time
		} else if (timeDiffMinutes <= -15) {
			return 'canStillJoin'; // More than 15 minutes after scheduled time
		}

		return 'waitingToJoin'; // More than 15 minutes before scheduled time
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-64 bg-white rounded-md shadow-md">
				<Loader2 className="h-10 w-10 animate-spin text-blue-600" />
			</div>
		);
	}

	const renderInterviewContent = () => {
		// Handle specific interview states based on API response code
		if (responseCode === InterviewStatusResponseCode.started) {
			// Interview is in progress
			return (
				<div className="rounded-xl p-8 flex flex-col items-center justify-center text-center space-y-4 bg-orange-50 border border-orange-100">
					<CalendarClock className="h-16 w-16 text-orange-600 mb-2" />
					<div className="space-y-2 mt-5">
						<h3 className="text-lg font-semibold text-gray-800">
							Interview In Progress
						</h3>
						<p className="text-sm text-gray-600 max-w-md">
							Your interview is currently in progress. Please return to the
							interview.
						</p>
						<p className="text-xs text-gray-500 mt-2">
							If you're experiencing any issues, please contact support at{' '}
							<a
								href="mailto:chandrakant@brivio.io"
								className="text-blue-600 hover:underline"
							>
								chandrakant@brivio.io
							</a>
						</p>
						{/* refresh button */}
						<Button
							onClick={() => window.location.reload()}
							className="bg-blue-600 hover:bg-blue-700 text-white cursor-pointer mt-4"
						>
							Refresh
						</Button>
					</div>
				</div>
			);
		}

		if (responseCode === InterviewStatusResponseCode.ended) {
			// Interview has ended
			return (
				<div className="rounded-xl p-8 flex flex-col items-center justify-center text-center space-y-4 bg-green-50 border border-green-100">
					<CalendarClock className="h-16 w-16 text-green-600 mb-2" />
					<div className="space-y-2 mt-5">
						<h3 className="text-lg font-semibold text-gray-800">
							Interview Completed
						</h3>
						<p className="text-sm text-gray-600 max-w-md">
							Your interview has been completed. We will be in touch with you
							shortly.
						</p>
						<p className="text-xs text-gray-500 mt-2">
							If you think this is a mistake, please contact support at{' '}
							<a
								href="mailto:chandrakant@brivio.io"
								className="text-blue-600 hover:underline"
							>
								chandrakant@brivio.io
							</a>
						</p>
					</div>
				</div>
			);
		}

		if (
			responseCode === InterviewStatusResponseCode.scheduled &&
			interviewStatus?.scheduledAt
		) {
			// Interview is scheduled
			const scheduledDate = new Date(interviewStatus.scheduledAt);

			// If showing reschedule form
			if (
				showRescheduleForm &&
				interviewStatus.availableSlots &&
				interviewStatus.availableSlots.length > 0
			) {
				const groupedSlots = groupSlotsByDate(interviewStatus.availableSlots);
				const availableDates = Object.keys(groupedSlots);
				const availableTimesForSelectedDate = selectedDate
					? groupedSlots[selectedDate] || []
					: [];

				return (
					<div className="rounded-xl p-8 space-y-6 bg-green-50 border border-green-100">
						<div className="flex flex-col items-center justify-center text-center space-y-4">
							<CalendarClock className="h-16 w-16 text-green-600 mb-2" />
							<div className="space-y-2 mt-5">
								<h3 className="text-lg font-semibold text-gray-800">
									Reschedule Your Interview
								</h3>
								<p className="text-sm text-gray-600 max-w-md">
									Current time:{' '}
									{scheduledDate.toLocaleDateString('en-US', {
										weekday: 'long',
										year: 'numeric',
										month: 'long',
										day: 'numeric',
										hour: 'numeric',
										minute: '2-digit',
									})}
								</p>
								<p className="text-sm text-gray-600 max-w-md">
									Please select your new preferred date and time.
								</p>
							</div>
						</div>

						<div className="space-y-4 max-w-md mx-auto">
							{/* Date Selection */}
							<div className="space-y-2">
								<label className="text-sm font-medium text-gray-700">
									Select New Date
								</label>
								<Select
									value={selectedDate}
									onValueChange={(value) => {
										setSelectedDate(value);
										setSelectedTime(''); // Reset time when date changes
									}}
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="Choose a date" />
									</SelectTrigger>
									<SelectContent>
										{availableDates.map((date) => (
											<SelectItem key={date} value={date}>
												{formatDate(date)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							{/* Time Selection */}
							<div className="space-y-2">
								<label className="text-sm font-medium text-gray-700">
									Select New Time
								</label>
								<Select
									value={selectedTime}
									onValueChange={setSelectedTime}
									disabled={!selectedDate}
								>
									<SelectTrigger className="w-full">
										<SelectValue
											placeholder={
												!selectedDate ? 'Select a date first' : 'Choose a time'
											}
										/>
									</SelectTrigger>
									<SelectContent>
										{availableTimesForSelectedDate.map((timestamp) => (
											<SelectItem key={timestamp} value={timestamp.toString()}>
												{formatTime(timestamp)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							{/* Action Buttons */}
							<div className="flex space-x-3">
								<Button
									onClick={() => {
										setShowRescheduleForm(false);
										setSelectedDate('');
										setSelectedTime('');
									}}
									variant="outline"
									className="flex-1"
								>
									Cancel
								</Button>
								<Button
									onClick={() => {
										posthog.capture(
											'InterviewDetailsSection_RescheduleInterviewClicked'
										);
										scheduleInterview();
									}}
									disabled={!selectedDate || !selectedTime || isScheduling}
									className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
								>
									{isScheduling ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin mr-2" />
											Rescheduling...
										</>
									) : (
										'Reschedule Interview'
									)}
								</Button>
							</div>
						</div>
					</div>
				);
			}

			// Regular scheduled view with time-based logic
			const joinState = getInterviewJoinState(interviewStatus.scheduledAt);
			const isValidBrowserAndSystem = isBrowserAndSystemValid();

			if (!isValidBrowserAndSystem) {
				posthog.capture('InterviewDetailsSection_BrowserCheckFailed');
			}

			return (
				<div className="rounded-xl p-8 flex flex-col items-center justify-center text-center space-y-6 bg-green-50 border border-green-100">
					<CalendarClock className="h-16 w-16 text-green-600 mb-2" />

					<div className="space-y-4">
						<h3 className="text-xl font-bold text-gray-800">
							Interview Scheduled
						</h3>

						{/* Prominent scheduled time display */}
						<div className="bg-white rounded-lg p-6 border-2 border-green-200 shadow-sm max-w-md mx-auto">
							<div className="space-y-2">
								<p className="text-xs font-medium text-green-700 uppercase tracking-wide">
									Scheduled Date & Time
								</p>
								<p className="text-2xl font-bold text-gray-900">
									{scheduledDate.toLocaleDateString('en-US', {
										weekday: 'long',
										month: 'long',
										day: 'numeric',
									})}
								</p>
								<p className="text-xl font-semibold text-gray-700">
									{scheduledDate.toLocaleDateString('en-US', {
										year: 'numeric',
									})}
								</p>
								<p className="text-2xl font-bold text-green-700">
									{scheduledDate.toLocaleTimeString('en-US', {
										hour: 'numeric',
										minute: '2-digit',
										hour12: true,
									})}
								</p>
								<p className="text-xs text-gray-500 mt-2">(Local time)</p>
							</div>
						</div>

						{/* Time-based messaging and join logic */}
						{joinState === 'interviewQueued' && (
							<div className="space-y-4">
								<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
									<p className="text-sm font-semibold text-amber-800">
										Interview is currently queued
									</p>
									<p className="text-xs text-amber-600 mt-1">
										Please wait approximately{' '}
										{interviewStatus?.minutesUntilQueueClears} minutes for your
										turn.
									</p>
								</div>
								<Button
									onClick={() => {
										posthog.capture(
											'InterviewDetailsSection_RefreshQueueStatusClicked'
										);
										window.location.reload();
									}}
									className="bg-gray-600 hover:bg-gray-700 text-white cursor-pointer"
								>
									Refresh Status
								</Button>
							</div>
						)}

						{joinState === 'canJoinEarly' && (
							<div className="space-y-4">
								<p className="text-sm font-semibold text-blue-700">
									You can join the interview before your scheduled time as well.
								</p>
								{!isValidBrowserAndSystem ? (
									<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
										<p className="text-sm font-medium text-yellow-800">
											Please use a desktop computer to join your interview.
										</p>
										<p className="text-xs text-yellow-600 mt-1">
											Mobile devices are not supported for interviews.
										</p>
									</div>
								) : (
									<Button
										onClick={() => {
											posthog.capture(
												'InterviewDetailsSection_EarlyJoinInterviewClicked'
											);
											navigate(`/lobby`);
										}}
										className="bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
									>
										Join Interview Early
									</Button>
								)}
							</div>
						)}

						{joinState === 'interviewStarted' && (
							<div className="space-y-4">
								<p className="text-sm font-semibold text-orange-700">
									Your interview has started, please join.
								</p>
								{!isValidBrowserAndSystem ? (
									<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
										<p className="text-sm font-medium text-yellow-800">
											Please use a desktop computer to join your interview.
										</p>
										<p className="text-xs text-yellow-600 mt-1">
											Mobile devices are not supported for interviews.
										</p>
									</div>
								) : (
									<Button
										onClick={() => {
											posthog.capture(
												'InterviewDetailsSection_NowJoinInterviewClicked'
											);
											navigate(`/lobby`);
										}}
										className="bg-orange-600 hover:bg-orange-700 text-white cursor-pointer"
									>
										Join Interview Now
									</Button>
								)}
							</div>
						)}

						{joinState === 'canStillJoin' && (
							<div className="space-y-4">
								<p className="text-sm font-semibold text-red-700">
									You can still interview. Please join through the button below.
								</p>
								{!isValidBrowserAndSystem ? (
									<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
										<p className="text-sm font-medium text-yellow-800">
											Please use a desktop computer to join your interview.
										</p>
										<p className="text-xs text-yellow-600 mt-1">
											Mobile devices are not supported for interviews.
										</p>
									</div>
								) : (
									<Button
										onClick={() => {
											posthog.capture(
												'InterviewDetailsSection_LateJoinInterviewClicked'
											);
											navigate(`/lobby`);
										}}
										className="bg-red-600 hover:bg-red-700 text-white cursor-pointer"
									>
										Join Interview
									</Button>
								)}
							</div>
						)}

						{joinState === 'waitingToJoin' && (
							<div className="space-y-4">
								<p className="text-sm text-gray-600 max-w-md mx-auto">
									Please join your interview at the scheduled time. You'll
									receive further instructions via email.
								</p>

								{/* Rescheduling logic - made less prominent */}
								{!interviewStatus.reschedulingAllowed ? (
									<p className="text-xs text-gray-400 mt-4">
										Rescheduling is not available for this interview.
									</p>
								) : !interviewStatus.availableSlots ||
								  interviewStatus.availableSlots.length === 0 ? (
									<p className="text-xs text-gray-400 mt-4">
										No slots are currently available for rescheduling.
									</p>
								) : (
									<div className="mt-6 pt-4 border-t border-green-200">
										<p className="text-xs text-gray-500 mb-2">
											Need to change your schedule?
										</p>
										<Button
											onClick={() => {
												posthog.capture(
													'InterviewDetailsSection_RescheduleInterviewClicked'
												);
												setShowRescheduleForm(true);
											}}
											variant="ghost"
											size="sm"
											className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 text-xs"
										>
											Reschedule Interview
										</Button>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			);
		}

		if (
			responseCode === InterviewStatusResponseCode.invited &&
			interviewStatus?.availableSlots &&
			interviewStatus.availableSlots.length > 0
		) {
			// Group available slots by date
			const groupedSlots = groupSlotsByDate(interviewStatus.availableSlots);
			const availableDates = Object.keys(groupedSlots);
			const availableTimesForSelectedDate = selectedDate
				? groupedSlots[selectedDate] || []
				: [];

			return (
				<div className="rounded-xl p-8 space-y-6 bg-blue-50 border border-blue-100">
					<div className="flex flex-col items-center justify-center text-center space-y-4">
						<CalendarClock className="h-16 w-16 text-blue-600 mb-2" />
						<div className="space-y-2 mt-5">
							<h3 className="text-lg font-semibold text-gray-800">
								Schedule Your Interview
							</h3>
							<p className="text-sm text-gray-600 max-w-md">
								Please select your preferred date and time for the interview.
							</p>
						</div>
					</div>

					<div className="space-y-4 max-w-md mx-auto">
						{/* Date Selection */}
						<div className="space-y-2">
							<label className="text-sm font-medium text-gray-700">
								Select Date
							</label>
							<Select
								value={selectedDate}
								onValueChange={(value) => {
									setSelectedDate(value);
									setSelectedTime(''); // Reset time when date changes
								}}
							>
								<SelectTrigger className="w-full">
									<SelectValue placeholder="Choose a date" />
								</SelectTrigger>
								<SelectContent>
									{availableDates.map((date) => (
										<SelectItem key={date} value={date}>
											{formatDate(date)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Time Selection */}
						<div className="space-y-2">
							<label className="text-sm font-medium text-gray-700">
								Select Time
							</label>
							<Select
								value={selectedTime}
								onValueChange={setSelectedTime}
								disabled={!selectedDate}
							>
								<SelectTrigger className="w-full">
									<SelectValue
										placeholder={
											!selectedDate ? 'Select a date first' : 'Choose a time'
										}
									/>
								</SelectTrigger>
								<SelectContent>
									{availableTimesForSelectedDate.map((timestamp) => (
										<SelectItem key={timestamp} value={timestamp.toString()}>
											{formatTime(timestamp)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Schedule Button */}
						<Button
							onClick={() => {
								posthog.capture(
									'InterviewDetailsSection_ScheduleInterviewClicked'
								);
								scheduleInterview();
							}}
							disabled={!selectedDate || !selectedTime || isScheduling}
							className="w-full bg-blue-600 hover:bg-blue-700 text-white"
						>
							{isScheduling ? (
								<>
									<Loader2 className="h-4 w-4 animate-spin mr-2" />
									Scheduling...
								</>
							) : (
								'Schedule Interview'
							)}
						</Button>
					</div>
				</div>
			);
		}

		if (responseCode === InterviewStatusResponseCode.invited) {
			// No slots available for scheduling
			return (
				<div className="rounded-xl p-8 flex flex-col items-center justify-center text-center space-y-4 bg-blue-50 border border-blue-100">
					<CalendarClock className="h-16 w-16 text-blue-600 mb-2" />
					<div className="space-y-2 mt-5">
						<h3 className="text-lg font-semibold text-gray-800">
							Interview Invitation Received
						</h3>
						<p className="text-sm text-gray-600 max-w-md">
							You have been invited for an interview. We are currently preparing
							available time slots. Please check back soon or contact us for
							more information.
						</p>
						<p className="text-xs text-gray-500 mt-2">
							For any questions, please contact support at{' '}
							<a
								href="mailto:chandrakant@brivio.io"
								className="text-blue-600 hover:underline"
							>
								chandrakant@brivio.io
							</a>
						</p>
					</div>
				</div>
			);
		}

		// Default state - no interview scheduled yet (when interview not found or other cases)
		return (
			<div className="rounded-xl p-8 flex flex-col items-center justify-center text-center space-y-4 bg-blue-50 border border-blue-100">
				<CalendarClock className="h-16 w-16 text-blue-600 mb-2" />
				<div className="space-y-2 mt-5">
					<h3 className="text-lg font-semibold text-gray-800">
						Thank You for Your Application
					</h3>
					<p className="text-sm text-gray-600 max-w-md">
						Thanks for providing your details. We are actively looking for
						organizations that match your profile. We will reach out to you via
						email if we find a match.
					</p>
				</div>
			</div>
		);
	};

	return (
		<div className="bg-white rounded-md shadow-lg p-6 space-y-6 border border-gray-100 transition-all">
			<div>
				<h2 className="text-xl font-semibold text-gray-800">
					Interview Details
				</h2>
				<p className="text-sm text-gray-500 mt-1">
					Below are the details of your interview.
				</p>
			</div>

			<Separator />

			{renderInterviewContent()}
		</div>
	);
};
